import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useAdBlockStore } from '../../store'
import { AdBlockResult, detectAdBlock, prettyLog } from '../../utils'

// Dedups the detection per path: the Providers hook and the page_load effect both
// request detection for the same page, but it runs only once.
let inflight: { path: string; promise: Promise<AdBlockResult> } | null = null

/**
 * Runs the AdBlock detection pipeline for a given path and updates the store.
 * Re-checks on every page entry (incl. SPA). Stale results (path changed mid-flight)
 * are dropped so they don't overwrite the current page's state.
 */
export function runAdBlockDetection(path: string): Promise<AdBlockResult> {
  if (inflight && inflight.path === path) {
    return inflight.promise
  }

  const promise = detectAdBlock().then(result => {
    if (inflight?.path === path) {
      useAdBlockStore.getState().setResult(result)
      // Console tracking for the final state update (Stage 2 requirement)
      prettyLog(
        'AdBlock',
        { ...result, path },
        result.adblock ? 'error' : 'info'
      )
    }
    return result
  })

  inflight = { path, promise }
  return promise
}

/**
 * Mounts AdBlock detection at the providers level so it runs on every page entry,
 * including SPA navigation. The result lives in adBlockStore.
 */
export const useAdBlockDetect = () => {
  const { asPath } = useRouter()

  useEffect(() => {
    runAdBlockDetection(asPath)
  }, [asPath])
}
